import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import qs from 'qs';

import { PageContext } from '../components/page';
import { Irregular } from '../../types/flight';
import { toTitleCase } from '../../lib/strings';
import { ViewFlightPath } from '../../appRoutes';
import { emptyFieldCharacter } from './FlightSummary';
import { iataOrFallBackToLabeledFS } from './FlightViewBody';

const IrregularOpsTypeMap: Record<string, string> = {
  CANCELLED: 'This flight is cancelled',
  CONTINUATION_FOR: 'This flight is a continuation of a',
  CONTINUED_BY: 'Another linked flight is the continuation of a',
  DIVERTED: 'This flight has been diverted to',
  FLOWN_OVER:
    "This flight is not expected to happen because the won't stop at the specified departure airport",
  FLYOVER: 'Aircraft flyover of the scheduled stop and continuing to',
  INCIDENT:
    "A confirmed incident on a flight status record means there has been a problem. When this is reported, it's because a Cirium employee has manually published a message for the flight. If this happens, you should ignore all status codes, positional updates, gate times, runway times, etc. We'll send a message explaining the problem",
  OTHER:
    "An irregular operation that doesn't qualify as one of the other operations listed here",
  RECOVERED:
    'A diverted flight has recovered and is continuing to its original destination',
  REINSTATED:
    'This flight was cancelled for a period of time and subsequently reinstated to operational status',
  REMOVED_FROM_SCHEDULE:
    'This flight was removed from the schedule and no other source sent any messages at all. After the scheduled arrival time, the flight is marked Cancelled',
  REPLACED_BY: 'This flight has been replaced by a',
  REPLACEMENT_FOR: 'This flight is the replacement for a',
  RETURN_FROM_AIR:
    'This flight has taken off and must return to its original departure',
  RETURN_TO_GATE:
    'The flight backed away from the gate, started to taxi, and then returned to the gate',
  SUBSEQUENT_OPERATION_BY:
    'The flight continued to its original destination in a',
  SUBSEQUENT_OPERATION_FOR:
    'This flight is continuing to the original destination of a',
};

/**
 * Gets the description for an irregular operation.
 * @param {Irregular} irregularOp - The irregular operation.
 * @param {string | undefined} carrier - The carrier.
 * @param {string | undefined} flightNumber - The flight number.
 * @returns {JSX.Element} The description.
 */
const getIrregularOpsDescription = (
  irregularOp: Irregular,
  carrier?: string,
  flightNumber?: string,
): JSX.Element => {
  const { type, arrivalDetails, linkedId } = irregularOp;
  if (type && type !== 'POSITIONAL_FOR') {
    const expectsLinkedId = [
      'CONTINUATION_FOR',
      'CONTINUED_BY',
      'REPLACED_BY',
      'REPLACEMENT_FOR',
      'SUBSEQUENT_OPERATION_BY',
      'SUBSEQUENT_OPERATION_FOR',
    ].includes(type);
    const expectsArrivalAirport = ['DIVERTED', 'FLYOVER'].includes(type);
    const potentialQueryParams = {
      carrier,
      flightNumber,
    };
    const potentialLink = linkedId ? (
      <Link
        to={`${ViewFlightPath.replace(':flightId', String(linkedId))}?${qs.stringify(
          potentialQueryParams,
        )}`}
      >
        {' '}
        <strong>linked flight</strong>
      </Link>
    ) : (
      ' an as-yet unlinked flight'
    );
    const potentialAirport = arrivalDetails
      ? ` ${iataOrFallBackToLabeledFS(arrivalDetails)}`
      : ' an unknown airport';
    const suffix = expectsLinkedId
      ? potentialLink
      : expectsArrivalAirport
      ? potentialAirport
      : '';
    return (
      <>
        {IrregularOpsTypeMap[type]}
        {suffix}
        {'.'}
      </>
    );
  } else {
    return <>{emptyFieldCharacter}</>;
  }
};

interface EventTimelineIrrOpProps {
  irregularOp: Irregular;
}

/**
 * EventTimelineIrrOp component to display irregular operation details.
 * @param {EventTimelineIrrOpProps} props - The props for the component.
 * @returns {JSX.Element} The EventTimelineIrrOp component.
 */
export const EventTimelineIrrOp: React.FC<EventTimelineIrrOpProps> = ({
  irregularOp,
}) => {
  const { query } = useContext(PageContext);
  const { flightNumber, carrier } = query;
  // this POSITIONAL_FOR check is redundant in case this component is ever used in another context
  if (irregularOp.type && irregularOp.type !== 'POSITIONAL_FOR') {
    const description = getIrregularOpsDescription(
      irregularOp,
      carrier,
      flightNumber,
    );
    const typeName = toTitleCase(irregularOp.type).replace(
      /^Other$/,
      'Miscellaneous',
    );
    return (
      <>
        <strong>{`${typeName}: `}</strong>
        {description}
      </>
    );
  } else {
    return <>{emptyFieldCharacter}</>;
  }
};